import { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import Card from '@mui/material/Card';
import TableRow from '@mui/material/TableRow';
import { Box, Button, Container, FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Select, TablePagination, TextField } from '@mui/material';
import Page from '../../components/Page';
import {
  setStatus,
  setLimit,
  setSkip,
  setCurrentPage,
  reset as resetBundleSubcategory,
} from '../../store/bundleSubcategory/bundleSubcategorySlice';
import { useAppSelector, useAppDispatch } from '../../store';
import {
  findBundleSubcategory,
  createBundleSubcategory,
  patchBundleSubcategory,
} from '../../store/bundleSubcategory/api';
import Loader from '../../components/Loader';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import { useFormik } from 'formik';
import useToggle from '../../hooks/useToggle';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Scrollbar from '../../components/Scrollbar';
import { omit } from 'lodash';
import BundleSubcategoryFormModal from './BundleSubcategoryFormModal';
import { BundleSubcategory } from '../../store/bundleSubcategory/api/interface';
import bundleSubcategorySchema from '../../validation/bundleSubcategorySchema';
import {
  reset as resetSelectBundleSubcategory,
  setStatus as setSelectBundleSubcategoryStatus,
} from '../../store/selectBundleSubcategory/selectBundleSubcategorySlice';
import { findSelectBundleCategory } from '../../store/selectBundleCategory/api';

const Index: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data, status, isLoading, totalRecords, limit, currentPage, skip } =
    useAppSelector((state) => state.bundleSubcategory);
  const categories = useAppSelector((state) => state.selectBundleCategory.data);
  const [search, setSearch] = useState<string>('');
  const [bundleCategoryId, setBundleCategoryId] = useState<string>('');
  const [activeStateFilter, setActiveStateFilter] = useState<string>('');
  const { toggle, onOpen, onClose } = useToggle();

  useEffect(() => {
    dispatch(findSelectBundleCategory({}));
  }, [dispatch]);

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findBundleSubcategory({
          limit,
          skip,
          ...(search && { search }),
          ...(bundleCategoryId && { bundleCategoryId }),
          ...(activeStateFilter !== '' && {
            activeState: activeStateFilter === 'true',
          }),
        })
      );
    }
  }, [dispatch, status]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setStatus({ status: 'idle' }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
    dispatch(setSkip({ skip: 0 }));
    dispatch(setCurrentPage({ currentPage: 0 }));
  };

  useEffect(() => {
    dispatch(resetBundleSubcategory());
  }, [search, bundleCategoryId, activeStateFilter]);

  const {
    handleSubmit,
    values,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
    setValues,
  } = useFormik({
    initialValues: {
      id: '',
      bundleCategoryId: '',
      labelEn: '',
      labelAr: '',
      imgUrl: '',
      position: '',
      activeState: true,
    },
    validationSchema: bundleSubcategorySchema,
    onSubmit: async (values) => {
      try {
        if (values.id) {
          await dispatch(patchBundleSubcategory(values)).unwrap();
        }
        if (!values.id) {
          const reqBody = omit(values, ['id']);
          await dispatch(createBundleSubcategory(reqBody)).unwrap();
        }
        onClose();
        resetForm();
        dispatch(setStatus({ status: 'idle' }));
        dispatch(resetSelectBundleSubcategory());
        dispatch(setSelectBundleSubcategoryStatus({ status: 'idle' }));
      } catch (error: any) {}
    },
  });

  const handleUpdate = (value: BundleSubcategory): void => {
    onOpen();
    setValues({
      id: value.id,
      bundleCategoryId: value.bundleCategoryId,
      labelEn: value?.labelEn,
      labelAr: value?.labelAr,
      imgUrl: value?.imgUrl || '',
      position: String(value?.position ?? ''),
      activeState: value?.activeState,
    });
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  const handleOpenCreate = (): void => {
    resetForm();
    onOpen();
  };

  return (
    <>
      <Page title={'Bundle Subcategory: List'}>
        <Container maxWidth={'xl'}>
          <HeaderBreadcrumbs
            heading={'Bundle Subcategory'}
            links={[
              { name: 'Dashboard', href: '/' },
              { name: 'Bundle Subcategory' },
            ]}
            action={
              <Button
                variant='contained'
                startIcon={<Iconify icon={'eva:plus-fill'} />}
                onClick={handleOpenCreate}
              >
                New Subcategory
              </Button>
            }
          />

          <Card>
            <Grid
              container
              spacing={2}
              alignItems='center'
              sx={{ py: 2.5, px: 3 }}
            >
              <Grid
                item
                xs={12}
                md={3}
              >
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    label='Category'
                    value={bundleCategoryId}
                    onChange={(e) => setBundleCategoryId(e.target.value)}
                  >
                    <MenuItem value=''>All Categories</MenuItem>
                    {categories?.map((category) => (
                      <MenuItem
                        key={category.id}
                        value={category.id}
                      >
                        {category.labelEn}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
              >
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label='Status'
                    value={activeStateFilter}
                    onChange={(e) => setActiveStateFilter(e.target.value)}
                  >
                    <MenuItem value=''>All Status</MenuItem>
                    <MenuItem value='true'>Active</MenuItem>
                    <MenuItem value='false'>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
              >
                <TextField
                  fullWidth
                  onChange={handleSearch}
                  placeholder='Search...'
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <Iconify
                          icon={'eva:search-fill'}
                          sx={{ color: 'text.disabled', width: 20, height: 20 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <Scrollbar>
              <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
                <Table size={'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>{'Category'}</TableCell>
                      <TableCell align='center'>{'Label EN'}</TableCell>
                      <TableCell align='center'>{'Label AR'}</TableCell>
                      <TableCell align='center'>{'Product Count'}</TableCell>
                      <TableCell align='center'>{'Position'}</TableCell>
                      <TableCell align='center'>{'Status'}</TableCell>
                      <TableCell align='center'>{'Action'}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {isLoading ? (
                      <TableRow hover>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : data?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          {'No record found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell align='center'>
                            {item?.bundle_category?.labelEn || '-'}
                          </TableCell>
                          <TableCell align='center'>{item?.labelEn}</TableCell>
                          <TableCell align='center'>{item?.labelAr}</TableCell>
                          <TableCell align='center'>
                            {item?.productCount ?? 0}
                          </TableCell>
                          <TableCell align='center'>{item?.position}</TableCell>
                          <TableCell align='center'>
                            <Label
                              variant='ghost'
                              color={item.activeState ? 'success' : 'error'}
                            >
                              {item.activeState ? 'Active' : 'InActive'}
                            </Label>
                          </TableCell>
                          <TableCell align='center'>
                            <IconButton
                              aria-label='edit'
                              onClick={() => handleUpdate(item)}
                            >
                              <Iconify icon={'eva:edit-fill'} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>

            <Box sx={{ position: 'relative' }}>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component='div'
                count={totalRecords}
                rowsPerPage={limit}
                page={Math.min(Math.round(totalRecords / limit), currentPage)}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          </Card>
          <BundleSubcategoryFormModal
            touched={touched}
            errors={errors}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onClose={onClose}
            resetForm={resetForm}
            setFieldValue={setFieldValue}
            toggle={toggle}
            values={values}
          />
        </Container>
      </Page>
    </>
  );
};

export default Index;
